import React, { useEffect, useState, useCallback } from 'react';
import api, { getCategories } from '../api';
import { formatCurrency } from '../utils/formatCurrency';

const PAYMENT_ICONS = { card: '💳', transfer: '↕', direct_debit: '🔁', cash: '💵' };

function SplitForm({ tx, categories, onDone, onCancel }) {
  const totalAbs = Math.abs(tx.amount);
  const [lines, setLines] = useState([
    { amount: '', category_id: null, subcategory_id: null },
    { amount: '', category_id: null, subcategory_id: null },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sumLines = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const remaining = Math.round((totalAbs - sumLines) * 100) / 100;

  const setLine = (i, field, val) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));

  const addLine = () => setLines(prev => [...prev, { amount: '', category_id: null, subcategory_id: null }]);
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const handleSplit = async () => {
    setError(null);
    if (Math.abs(remaining) > 0.02) {
      setError(`Total des lignes (${sumLines.toFixed(2)} €) ≠ montant original (${totalAbs.toFixed(2)} €) — reste ${remaining.toFixed(2)} €`);
      return;
    }
    if (lines.some(l => !l.subcategory_id)) {
      setError('Toutes les lignes doivent avoir une sous-catégorie');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/transactions/${tx.id}/split`, {
        splits: lines.map(l => ({
          amount: parseFloat(l.amount),
          category_id: l.category_id,
          subcategory_id: l.subcategory_id,
        })),
      });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const sel = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', flex: 1 };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
        Diviser {formatCurrency(Math.abs(tx.amount))}
      </div>

      {lines.map((line, i) => {
        const cat = categories.find(c => c.id === line.category_id);
        return (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <input
              type="number" min="0.01" step="0.01" placeholder="Montant"
              value={line.amount}
              onChange={e => setLine(i, 'amount', e.target.value)}
              style={{ width: 100, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>€</span>
            <select
              style={sel}
              value={line.category_id || ''}
              onChange={e => setLine(i, 'category_id', Number(e.target.value) || null)}
            >
              <option value="">Catégorie…</option>
              {categories.filter(c => c.subcategories?.length > 0).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select
              style={sel}
              value={line.subcategory_id || ''}
              onChange={e => setLine(i, 'subcategory_id', Number(e.target.value) || null)}
              disabled={!cat}
            >
              <option value="">Sous-catégorie…</option>
              {(cat?.subcategories || []).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {lines.length > 2 && (
              <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, padding: '0 4px' }}>✕</button>
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <button onClick={addLine} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--border)', color: 'var(--text)', border: 'none', fontSize: 13, cursor: 'pointer' }}>
          + Ligne
        </button>
        <span style={{ fontSize: 13, color: Math.abs(remaining) < 0.02 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
          {Math.abs(remaining) < 0.02 ? '✓ Équilibré' : `Reste ${remaining.toFixed(2)} €`}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={onCancel} style={{ padding: '7px 16px', borderRadius: 8, background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontSize: 13, cursor: 'pointer' }}>
            Annuler
          </button>
          <button
            onClick={handleSplit}
            disabled={saving || Math.abs(remaining) > 0.02}
            style={{ padding: '7px 16px', borderRadius: 8, background: saving || Math.abs(remaining) > 0.02 ? 'var(--border)' : 'var(--primary)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}
          >
            {saving ? 'Division…' : 'Diviser'}
          </button>
        </div>
      </div>
      {error && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
    </div>
  );
}

export default function CategorizationQueue({ onUpdated }) {
  const [queue, setQueue]         = useState([]);
  const [idx, setIdx]             = useState(0);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [skipped, setSkipped]     = useState(new Set());
  const [done, setDone]           = useState(0);
  const [splitting, setSplitting] = useState(false);

  const load = useCallback(async () => {
    const [txRes, catRes] = await Promise.all([
      api.get('/transactions/to-review'),
      getCategories(),
    ]);
    setQueue(txRes.data);
    setCategories(catRes.data);
    setIdx(0);
    setSelectedCat(null);
    setSkipped(new Set());
    setDone(0);
    setSplitting(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Raccourcis clavier
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      const n = parseInt(e.key, 10);
      if (!isNaN(n) && n >= 1) {
        if (!selectedCat) {
          const visibleCats = categories.filter(c => c.subcategories?.length > 0);
          if (visibleCats[n - 1]) setSelectedCat(visibleCats[n - 1]);
        } else {
          const subs = selectedCat.subcategories || [];
          if (subs[n - 1]) categorize(subs[n - 1].id, selectedCat.id);
        }
      }
      if (e.key === 'Escape') setSelectedCat(null);
      if (e.key === 'ArrowRight') skip();
      if (e.key === 'Backspace') setSelectedCat(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat, categories, idx, queue]);

  const currentTx = queue[idx];

  const categorize = async (subcategoryId, categoryId) => {
    if (!currentTx || saving) return;
    setSaving(true);
    try {
      await api.patch(`/transactions/${currentTx.id}/categorize`, {
        category_id: categoryId,
        subcategory_id: subcategoryId,
      });
      setDone(d => d + 1);
      onUpdated?.();
      advance();
    } catch (err) {
      console.error('Erreur catégorisation:', err);
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    if (!currentTx) return;
    setSkipped(s => new Set([...s, currentTx.id]));
    advance();
  };

  const advance = () => {
    setSelectedCat(null);
    setSplitting(false);
    setIdx(i => {
      let next = i + 1;
      while (next < queue.length && skipped.has(queue[next]?.id)) next++;
      return next;
    });
  };

  const remaining = queue.length - done - skipped.size;
  const isFinished = !currentTx || idx >= queue.length;

  if (queue.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-soft)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
          Toutes les transactions sont catégorisées
        </div>
      </div>
    );
  }

  if (isFinished) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Session terminée
        </div>
        <div style={{ color: 'var(--text-soft)', marginBottom: 24 }}>
          {done} catégorisée{done > 1 ? 's' : ''} · {skipped.size} ignorée{skipped.size > 1 ? 's' : ''}
        </div>
        <button
          onClick={load}
          style={{ padding: '10px 24px', borderRadius: 8, background: 'var(--primary)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
        >
          Recommencer
        </button>
      </div>
    );
  }

  const visibleCats = categories.filter(c => c.subcategories?.length > 0);
  const history = currentTx?.merchant_history || [];
  const isMulti = currentTx?.multi_category;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>

      {/* Barre de progression */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-soft)', marginBottom: 6 }}>
          <span>{done} catégorisée{done > 1 ? 's' : ''}</span>
          <span>{remaining} restante{remaining > 1 ? 's' : ''} sur {queue.length}</span>
        </div>
        <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 3, background: 'var(--primary)',
            width: `${Math.round((done / queue.length) * 100)}%`, transition: 'width .3s',
          }} />
        </div>
      </div>

      {/* Carte transaction */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 14, padding: '20px 24px', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
              {new Date(currentTx.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              {' · '}{PAYMENT_ICONS[currentTx.payment_method] || ''} {currentTx.payment_method}
              {isMulti && <span style={{ marginLeft: 8, background: '#fef9c3', color: '#a16207', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>multi-catégorie</span>}
            </div>
            <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--text)', marginBottom: 4, wordBreak: 'break-word' }}>
              {currentTx.merchant || currentTx.raw_label}
            </div>
            {currentTx.merchant && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                {currentTx.raw_label}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#dc2626', whiteSpace: 'nowrap' }}>
              {formatCurrency(currentTx.amount)}
            </div>
            <button
              onClick={() => { setSplitting(s => !s); setSelectedCat(null); }}
              style={{
                padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: splitting ? 'var(--primary)' : 'none',
                color: splitting ? '#fff' : 'var(--primary)',
                border: '1px solid var(--primary)', cursor: 'pointer',
              }}
            >
              ✂ Diviser
            </button>
          </div>
        </div>
      </div>

      {/* Formulaire split */}
      {splitting && (
        <SplitForm
          tx={currentTx}
          categories={categories}
          onDone={() => { setDone(d => d + 1); onUpdated?.(); load(); }}
          onCancel={() => setSplitting(false)}
        />
      )}

      {/* Chips historique marchand */}
      {!splitting && history.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
            Utilisé souvent pour
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {history.map((h, i) => (
              <button
                key={i}
                onClick={() => categorize(h.subcategory_id, h.category_id)}
                disabled={saving}
                style={{
                  padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  border: '1.5px solid var(--success)', background: '#f0fdf4', color: '#166534',
                  cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {h.subcategory_name}
                <span style={{ fontSize: 10, background: '#dcfce7', borderRadius: 4, padding: '1px 5px', color: '#15803d' }}>{h.cnt}×</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sélection catégorie */}
      {!splitting && !selectedCat && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>
            {history.length > 0 ? 'Autre catégorie' : 'Catégorie'}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11 }}> (touches 1–{Math.min(visibleCats.length, 9)})</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {visibleCats.map((cat, i) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCat(cat)}
                style={{
                  padding: '8px 14px', borderRadius: 20, border: '1.5px solid var(--border)',
                  background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
                  fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {i < 9 && (
                  <span style={{ fontSize: 10, background: 'var(--border)', borderRadius: 4, padding: '1px 5px', color: 'var(--text-muted)', fontWeight: 700 }}>
                    {i + 1}
                  </span>
                )}
                {cat.name}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Sélection sous-catégorie */}
      {!splitting && selectedCat && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <button
              onClick={() => setSelectedCat(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 13, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              ← {selectedCat.name}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>(Échap pour changer)</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {(selectedCat.subcategories || []).map((sub, i) => (
              <button
                key={sub.id}
                onClick={() => categorize(sub.id, selectedCat.id)}
                disabled={saving}
                style={{
                  padding: '8px 14px', borderRadius: 20, border: '1.5px solid var(--primary)',
                  background: '#eff6ff', color: 'var(--primary)', cursor: saving ? 'wait' : 'pointer',
                  fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {i < 9 && (
                  <span style={{ fontSize: 10, background: '#dbeafe', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>
                    {i + 1}
                  </span>
                )}
                {sub.name}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Actions bas */}
      {!splitting && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={skip}
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', color: 'var(--text-soft)', fontSize: 13 }}
          >
            Ignorer →
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {idx + 1} / {queue.length} · → ignorer · Échap retour
          </span>
        </div>
      )}

    </div>
  );
}
